import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "../utils/toast";
import { startWorkflowCase } from "../api";
import { wmErrorMessage } from "../api/wm-client";
import { NAWY_NOW_LINE_OF_BUSINESS } from "../constants";
import type { CloneFromRequest } from "../dtos";

export interface StartWorkflowInput {
  /** CRM customer uid — crm-backend loads the customer server-side to build the participant. */
  customerUid?: string;
  leadUid: string;
  /** §Clone-steps: prefill the new application from another one on the SAME lead. Omitted ⇒ an
   * ordinary empty start, exactly as before. */
  cloneFrom?: CloneFromRequest;
  /** §Clone-steps: a uuid v4 minted once per "Start another application" dialog opening (stable
   * across retries of that same submission) so a double-submit never starts two applications. */
  idempotencyKey?: string;
}

/**
 * Trigger a new flow instance for the current lead through crm-backend
 * (POST /customers/:uid/workflow-cases): the backend sources the customer payload
 * itself, so the workflow-manager case is always created WITH its PRIMARY
 * participant — same as the client channel. Success invalidates the whole
 * nawy-now query tree so the tab reloads its board, and resolves the new case
 * (`CaseWorkflowView`) so the caller can select it.
 */
export const useStartWorkflow = () => {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: ({ customerUid, leadUid, cloneFrom, idempotencyKey }: StartWorkflowInput) => {
      if (!customerUid) {
        return Promise.reject(
          new Error("This lead has no customer to start the flow for.")
        );
      }
      return startWorkflowCase(customerUid, {
        leadUid,
        lineOfBusiness: NAWY_NOW_LINE_OF_BUSINESS,
        ...(cloneFrom ? { cloneFrom } : {}),
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }).then((response) => response.data);
    },
    // §Multi-application: AWAITED, so a caller's own `mutate(…, {onSuccess})` — which react-query runs only
    // after this one settles — can select the new application knowing the refetched list already has it
    // (otherwise the page would fall back to the previous case for a beat).
    onSuccess: async () => {
      toast.success("Nawy Now flow started");
      await queryClient.invalidateQueries({ queryKey: ["nawy-now"] });
    },
    onError: (error: unknown) => toast.error(wmErrorMessage(error)),
  });

  return { create };
};
